import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Zap, ArrowRight } from 'lucide-react';
import { getPageContent, getCaseStudies } from '../utils/contentManager';

const CaseStudies = () => {
  const [pageContent, setPageContent] = useState({});
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    setPageContent(getPageContent('caseStudies'));
    setCaseStudies(getCaseStudies());
  }, []);

  const industryStats = [
    { industry: 'Technology', projects: '25+', avgROI: '280%' },
    { industry: 'E-commerce', projects: '15+', avgROI: '320%' },
    { industry: 'Healthcare', projects: '12+', avgROI: '240%' },
    { industry: 'Manufacturing', projects: '18+', avgROI: '350%' },
    { industry: 'Financial Services', projects: '10+', avgROI: '290%' },
    { industry: 'Consulting', projects: '20+', avgROI: '310%' }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 102, 255, 0.9), rgba(0, 51, 153, 0.9)), url('https://images.unsplash.com/photo-1513530534585-c7b1394c6d51?w=1920&h=1080&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {pageContent.title || 'Case'} <span className="text-indigo-200">Studies</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100">
              {pageContent.subtitle ||
                'Real success stories showcasing how our AI solutions and strategic consulting have transformed businesses across industries.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industry Statistics */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {pageContent.impactTitle || 'Industry'} <span className="text-gradient">Impact</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {pageContent.impactSubtitle ||
                'Our proven track record across diverse industries demonstrates the versatility and effectiveness of our solutions.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industryStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="text-center p-4 glass-effect rounded-xl"
              >
                <div className="text-sm font-semibold text-gray-700 mb-2">{stat.industry}</div>
                <div className="text-2xl font-bold text-blue-800 mb-1">{stat.projects}</div>
                <div className="text-xs text-gray-500">Projects</div>
                <div className="text-lg font-bold text-green-600 mt-2">{stat.avgROI}</div>
                <div className="text-xs text-gray-500">Avg ROI</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.storiesTitle || 'Success'} <span className="text-gradient">Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent.storiesSubtitle ||
                "Discover how we've helped businesses achieve remarkable transformations and measurable results."}
            </p>
          </motion.div>

          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id || index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className="bg-white rounded-3xl shadow-premium overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  <div className="p-8 lg:p-12">
                    {study.industry && (
                      <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-6">
                        {study.industry}
                      </div>
                    )}
                    <h3 className="text-3xl font-bold text-gray-900 mb-6">{study.title}</h3>

                    <div className="space-y-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Challenge</h4>
                        <p className="text-gray-600 leading-relaxed">{study.challenge}</p>
                      </div>

                      {study.solution && (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">Solution</h4>
                          <p className="text-gray-600 leading-relaxed">{study.solution}</p>
                        </div>
                      )}

                      {study.results && study.results.length > 0 && (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">Results</h4>
                          <div className="grid grid-cols-2 gap-4">
                            {study.results.map((result, idx) => (
                              <div key={idx} className="bg-blue-50 p-4 rounded-xl text-center">
                                <div className="text-2xl font-bold text-blue-600 mb-1">{result.metric}</div>
                                <div className="text-sm text-gray-600">{result.description}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {study.testimonial && study.testimonial.text && (
                        <div className="bg-gray-50 p-6 rounded-2xl">
                          <blockquote className="text-gray-700 italic mb-4">"{study.testimonial.text}"</blockquote>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {study.testimonial.author}
                            </div>
                            <div className="text-gray-600 text-sm">{study.testimonial.role}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {study.image && (
                    <div className="bg-gray-100">
                      <img src={study.image} alt={study.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.frameworkTitle || 'Our Success'} <span className="text-gradient">Framework</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent.frameworkSubtitle || 'The proven methodology behind every successful transformation.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Deep Discovery',
                description:
                  'We immerse ourselves in your business to understand challenges, opportunities, and goals at the deepest level.'
              },
              {
                icon: Zap,
                title: 'Strategic Implementation',
                description:
                  'Custom AI solutions and strategic initiatives designed specifically for your business context and objectives.'
              },
              {
                icon: TrendingUp,
                title: 'Measurable Results',
                description:
                  'Continuous optimization and measurement to ensure sustained growth and maximum return on investment.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                whileHover={{ y: -10 }}
                className="text-center p-8 glass-effect rounded-2xl hover:shadow-glow transition-all duration-300"
              >
                <div className="gradient-background w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="space-y-8">
            <h2 className="text-4xl md:text-6xl font-bold text-white">
              {pageContent.ctaTitle || 'Ready to Write Your Success Story?'}
            </h2>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
              {pageContent.ctaSubtitle ||
                'Join the ranks of businesses that have achieved extraordinary results with our solutions.'}
            </p>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(255,255,255,0.3)' }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-800 px-12 py-4 rounded-xl font-bold text-xl shadow-premium hover:shadow-glow
              transition-all duration-300 inline-flex items-center space-x-3"
            >
              <span>{pageContent.ctaButtonText || 'Get Free Basic Consultation'}</span>
              <ArrowRight size={24} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
